"use client";

import axios from "axios";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { useMe } from "@/hooks/api/useMe";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type LeadRow = {
  id: number;
  company_name: string;
  gstin?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  contact_person?: string | null;
  status: "new" | "contacted" | "qualified" | "converted" | "lost";
  notes?: string | null;
  created_by_user_id?: number | null;
  created_at?: string | null;
  source?: "staff" | "web" | string | null;
  linked_company_id?: number | null;
  linked_company_name?: string | null;
};

type CompanyRow = {
  id: number;
  name: string;
};

const STATUSES: LeadRow["status"][] = [
  "new",
  "contacted",
  "qualified",
  "converted",
  "lost",
];

export default function ProductAdminLeadsPage() {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const [companyName, setCompanyName] = useState("");
  const [gstin, setGstin] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [linkPick, setLinkPick] = useState<Record<number, string>>({});

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["product-admin", "leads"],
    queryFn: async () => {
      const { data } = await api.get<{ leads: LeadRow[] }>("/api/product-admin/leads");
      return data.leads;
    },
  });

  const { data: companies = [] } = useQuery({
    queryKey: ["product-admin", "companies"],
    queryFn: async () => {
      const { data } = await api.get<{ companies: CompanyRow[] }>("/api/product-admin/companies");
      return data.companies;
    },
  });

  const companyById = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of companies) {
      m.set(c.id, c.name);
    }
    return m;
  }, [companies]);

  const createLead = useMutation({
    mutationFn: async () => {
      await api.post("/api/product-admin/leads", {
        company_name: companyName,
        gstin,
        address,
        phone,
        email,
        contact_person: contactPerson,
        notes,
      });
    },
    onSuccess: async () => {
      setCompanyName("");
      setGstin("");
      setAddress("");
      setPhone("");
      setEmail("");
      setContactPerson("");
      setNotes("");
      setErr(null);
      await qc.invalidateQueries({ queryKey: ["product-admin", "leads"] });
      await qc.invalidateQueries({ queryKey: ["product-admin", "dashboard"] });
    },
    onError: (e: unknown) => {
      let msg = "Could not create lead.";
      if (axios.isAxiosError(e)) {
        const data = e.response?.data as { error?: string } | undefined;
        msg = data?.error ?? msg;
      }
      setErr(msg);
    },
  });

  const updateStatus = useMutation({
    mutationFn: async (vars: { id: number; status: LeadRow["status"] }) => {
      await api.put(`/api/product-admin/leads/${vars.id}`, { status: vars.status });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["product-admin", "leads"] });
      await qc.invalidateQueries({ queryKey: ["product-admin", "dashboard"] });
    },
  });

  const linkLead = useMutation({
    mutationFn: async (vars: { id: number; companyId: number }) => {
      await api.put(`/api/product-admin/leads/${vars.id}`, { linked_company_id: vars.companyId });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["product-admin", "leads"] });
    },
  });

  const delLead = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/product-admin/leads/${id}`);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["product-admin", "leads"] });
    },
  });

  function canDeleteLead(l: LeadRow): boolean {
    if (me?.role === "product_admin") {
      return true;
    }
    if (me?.role === "product_admin_staff" && me.id != null) {
      return l.created_by_user_id === me.id;
    }

    return false;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Leads</h1>
        <p className="text-sm text-muted-foreground">
          Staff sees their own leads plus <strong className="text-foreground">web inquiries</strong> from the
          marketing site. Product admin sees everything. Link a lead to a company record after you create their
          tenant.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Add company lead</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2 sm:col-span-2">
            <Label>Company name</Label>
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="ACME Pvt Ltd"
            />
          </div>
          <div className="grid gap-2">
            <Label>GSTIN</Label>
            <Input value={gstin} onChange={(e) => setGstin(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>Contact person</Label>
            <Input
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label>Phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="grid gap-2 sm:col-span-2">
            <Label>Address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
          <div className="grid gap-2 sm:col-span-2">
            <Label>Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {err ? <p className="text-sm text-destructive sm:col-span-2">{err}</p> : null}
          <div className="sm:col-span-2">
            <Button
              type="button"
              disabled={createLead.isPending || !companyName}
              onClick={() => createLead.mutate()}
            >
              {createLead.isPending ? "Saving…" : "Add lead"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>All leads</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[960px] text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-3">ID</th>
                    <th className="py-2 pr-3">Source</th>
                    <th className="py-2 pr-3">Company</th>
                    <th className="py-2 pr-3">GSTIN</th>
                    <th className="py-2 pr-3">Contact</th>
                    <th className="py-2 pr-3">Phone</th>
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">Linked</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Created</th>
                    <th className="py-2 pr-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => {
                    const src = l.source === "web" ? "web" : "staff";
                    const linkedId = l.linked_company_id ?? null;
                    const linkedName =
                      l.linked_company_name ?? (linkedId ? companyById.get(linkedId) : undefined);
                    const pick = linkPick[l.id] ?? "";
                    const showConnect = !linkedId && companies.length > 0;

                    return (
                      <tr key={l.id} className="border-b last:border-0">
                        <td className="py-2 pr-3">{l.id}</td>
                        <td className="py-2 pr-3">
                          {src === "web" ? (
                            <Badge variant="secondary" className="font-normal">
                              Web
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="font-normal">
                              Staff
                            </Badge>
                          )}
                        </td>
                        <td className="py-2 pr-3 font-medium">{l.company_name}</td>
                        <td className="py-2 pr-3">{l.gstin ?? ""}</td>
                        <td className="py-2 pr-3">{l.contact_person ?? ""}</td>
                        <td className="py-2 pr-3">{l.phone ?? ""}</td>
                        <td className="py-2 pr-3">{l.email ?? ""}</td>
                        <td className="py-2 pr-3">
                          {linkedId ? (
                            <Link
                              to={`/product-admin/companies/${linkedId}`}
                              className="text-primary underline-offset-2 hover:underline"
                            >
                              {linkedName ?? `#${linkedId}`}
                            </Link>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <select
                            className="h-7 rounded-md border border-border bg-background px-2 text-xs"
                            value={l.status}
                            onChange={(e) =>
                              updateStatus.mutate({
                                id: l.id,
                                status: e.target.value as LeadRow["status"],
                              })
                            }
                          >
                            {STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 pr-3">{l.created_at ?? ""}</td>
                        <td className="py-2 pr-3 text-right">
                          <div className="flex flex-col items-end gap-1.5">
                            {showConnect ? (
                              <div className="flex flex-wrap items-center justify-end gap-1">
                                <select
                                  className="h-7 max-w-[140px] rounded-md border border-border bg-background px-1 text-xs"
                                  value={pick}
                                  onChange={(e) =>
                                    setLinkPick((prev) => ({ ...prev, [l.id]: e.target.value }))
                                  }
                                >
                                  <option value="">Company…</option>
                                  {companies.map((c) => (
                                    <option key={c.id} value={String(c.id)}>
                                      {c.name}
                                    </option>
                                  ))}
                                </select>
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  disabled={linkLead.isPending || !pick}
                                  onClick={() => {
                                    const cid = Number(pick);
                                    if (!cid) {
                                      return;
                                    }
                                    linkLead.mutate(
                                      { id: l.id, companyId: cid },
                                      {
                                        onSuccess: () => {
                                          setLinkPick((prev) => {
                                            const next = { ...prev };
                                            delete next[l.id];
                                            return next;
                                          });
                                        },
                                      },
                                    );
                                  }}
                                >
                                  Connect
                                </Button>
                              </div>
                            ) : null}
                            {canDeleteLead(l) ? (
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                disabled={delLead.isPending}
                                onClick={() => delLead.mutate(l.id)}
                              >
                                Delete
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {leads.length === 0 ? (
                    <tr>
                      <td className="py-3 text-muted-foreground" colSpan={11}>
                        No leads yet.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
